import {Injectable} from "@angular/core";
import {NavigationStart, Router} from "@angular/router";
import {IRemoteLockResult, User} from "../definitions";
import {WatchService} from "./watch.service";
import {Observable} from "rxjs/Observable";
import {CollectionService} from "./collection.service";
import {QueryService} from "./query.service";
import {LockService} from "./lock.service";


import "rxjs/add/operator/filter";
import "rxjs/add/observable/of";
import "rxjs/add/observable/zip";
import {BusService, INewDocumentEvent} from "./bus.service";
import {paramsHelper} from "../helpers/params-helper";
import {IDocument} from "../interfaces/document.definition";
import {E_ENDPOINTS} from "../helpers/params/loader-params";
import {HistoryService} from "./history.service";


@Injectable()
export class DocumentService {
  private _document: IDocument;
  private _readonly: boolean;
  private _user: User;


  constructor(private router: Router,
              private watchService: WatchService,
              private collectionService: CollectionService,
              private queryService: QueryService,
              private lockService: LockService,
              private busService: BusService,
              private historyService: HistoryService) {
    this.router.events
      .filter(e => e instanceof NavigationStart)
      .subscribe((): void => this.reset());

    this.busService
      .onDocumentLockRemoved
      .subscribe(documentId => this.onLockRemoved(documentId));

    this.watchService.docChanged()
      .subscribe((): void => this.onDocChanged());
  }


  load(endpointId:E_ENDPOINTS, documentType: string, documentId: string) {
    let zip = [];
    if (this.hasDocument && this.editable) {
      console.log(`unlock ${this.document._id}`);
      zip.push(this.lockService.unlock(this.document._id));
    } else {
      zip.push(Observable.of(true));
    }

    const params = paramsHelper.loader()
      .setEndpointId(endpointId)
      .setObjectType(documentType)
      .setAdditionalParam("_id", documentId);


   //zip.push(this.lockService.lock(documentId));
    zip.push(this.collectionService.loadOne(params));

    Observable.zip.apply(Observable, zip)
      .subscribe(([unlockSuccess, /*lockResult, */loadResult]: [boolean, /*IRemoteLockResult, */IDocument]) => {
        if (!loadResult) {
          return;
        }

        this.setEditable(<IDocument>loadResult);
      });
  }


  save(endpointId:E_ENDPOINTS) {
    const params = paramsHelper.loader()
      .setEndpointId(endpointId);
    this.collectionService.saveDocument(params, this._document)
      .subscribe((): void => {
        this.emitDocumentSaved();
        this.watchService.reset();
        this.historyService.saveHistory(this._document);
      });
  }


  reset() {
    if (this.document && this.editable) {
      this.lockService.unlock(this.document._id).subscribe();
    }
    this.clearDocument();
  }


  duplicate() {
    const params = paramsHelper.loader()
      .setEndpointApi()
      .setAdditionalParam("_id", this.document._id)
      .setObjectType(this.document._type);

    this.collectionService.duplicate(params)
      .subscribe((): void => this.emitDocumentSaved());
  }


  addNew(endpointId:E_ENDPOINTS, type: string, subtype: string = null) {
    this.reset();

    const params = paramsHelper.loader()
      .setEndpointId(endpointId)
      .setObjectType(type);

    if (subtype !== null) {
      params.setAdditionalParam("_subtype", subtype);
    }

    this.collectionService.createOne(params)
      .subscribe((result: any): void => {
        this.emitDocumentSaved(); // note: when emitted nv-collection reloads list
        this.historyService.saveHistory(result.object);
        this.load(endpointId, type, result.insertedId);
      });
  }


  remove(endpointId:E_ENDPOINTS) {
    if (this.document && this.editable) {
      const documentId = this._document._id;
      const params = paramsHelper.loader()
        .setEndpointId(endpointId)
        .setAdditionalParam("_id", documentId);

      this.collectionService.removeDocument(params)
        .subscribe((): void => {
          this.emitDocumentSaved();
          this.lockService.unlock(documentId).subscribe();
          this.historyService.saveHistory(this._document);
          this.clearDocument();
        });
    }
  }

  private onDocChanged() {
    console.log('on doc changed')
    let zip = [];

    if (this.document != undefined && this.watchService.hasChanges)
    {
      console.log(`lock ${this.document._id}`);
      zip.push(this.lockService.lock(this.document._id.toString()));
    }

    Observable.zip.apply(Observable, zip)
      .subscribe(([lockResult]: [IRemoteLockResult]) => {

        if (!lockResult.result) {
          this.setReadOnly(this.document, lockResult.user);
          return;
        }

      });
    this.onDocumentUpdated();
  }


  private onLockRemoved(documentId: string) {
    if (this.document
      && this.document._id === documentId
      && this.editable) {
      console.log('remove lock');
      this.setDocumentImpl(this._document, true); // make readonly
    }
  }


  private onDocumentUpdated() {
    if (!(this.document && this.editable)) {
      return;
    }
    console.log('doc updated');
    this.lockService.prolong(this.document._id);
  }


  get readonly(): boolean {
    return this._readonly;
  }


  get editable(): boolean {
    return !this.readonly;
  }


  get hasDocument(): boolean {
    return !!this._document;
  }


  get document(): IDocument {
    return this._document;
  }


  private setEditable(document: IDocument) {
    console.log('set editable');
    this.setDocumentImpl(document, false);
  }


  private setReadOnly(document: IDocument, user: User) {
    console.log('remove readonly');
    this.setDocumentImpl(document, true, user);
  }


  private setDocumentImpl(document: IDocument, readonly: boolean, user: User = null) {
    console.log('set');
    this._document = document;
    this._readonly = readonly;
    this._user = user;

    this.watchService.reset();
    this.queryService.update(document);
    this.emitDocumentUpdated();
  }


  clearDocument() {
    console.log('clear');
    this._readonly = false;
    this._document = null;

    this.watchService.reset();
    this.queryService.reset();
    this.emitDocumentUpdated();
  }


  emitDocumentUpdated() {
    this.busService.emitDocumentUpdated(this.createDocumentUpdatedEvent());
  }


  private emitDocumentSaved() {
    this.busService.emitDocumentSaved();
  }


  createDocumentUpdatedEvent(): INewDocumentEvent {
    return {
      document: this.document,
      readonly: this.readonly,
      user: this._user ? this._user : <any>{displayName: "system"}
    };
  }
}



// WEBPACK FOOTER //
// /opt/app/develop/src/app/services/document.service.ts